import { useEffect, useRef, useCallback } from 'react'

export interface UseAutoSaveOptions<T> {
  data: T
  onSave: (data: T) => Promise<void> | void
  debounceMs?: number
  enabled?: boolean
  onBeforeUnload?: boolean
}

/**
 * Универсальный хук для автосохранения данных формы
 */
export function useAutoSave<T>({
  data,
  onSave,
  debounceMs = 500,
  enabled = true,
  onBeforeUnload = true,
}: UseAutoSaveOptions<T>) {
  const dataRef = useRef<T>(data)
  const saveTimeoutRef = useRef<NodeJS.Timeout>()
  const isSavingRef = useRef(false)
  const hasPendingSaveRef = useRef(false)

  useEffect(() => {
    dataRef.current = data
  }, [data])

  const executeSave = useCallback(async (dataToSave: T) => {
    if (!enabled || isSavingRef.current) {
      return
    }

    try {
      isSavingRef.current = true
      hasPendingSaveRef.current = false
      await onSave(dataToSave)
    } catch (error) {
      console.error('AutoSave error:', error)
    } finally {
      isSavingRef.current = false
    }
  }, [enabled, onSave])

  const scheduleSave = useCallback(() => {
    if (!enabled) {
      return
    }

    hasPendingSaveRef.current = true

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }

    saveTimeoutRef.current = setTimeout(() => {
      if (dataRef.current) {
        executeSave(dataRef.current)
      }
    }, debounceMs)
  }, [debounceMs, enabled, executeSave])

  const saveImmediately = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }

    if (enabled && dataRef.current && (hasPendingSaveRef.current || saveTimeoutRef.current)) {
      const savePromise = onSave(dataRef.current)
      if (savePromise instanceof Promise) {
        savePromise.catch(error => {
          console.error('AutoSave immediate error:', error)
        })
      }
      hasPendingSaveRef.current = false
    }
  }, [enabled, onSave])

  useEffect(() => {
    if (enabled) {
      scheduleSave()
    }
  }, [data, enabled, scheduleSave])

  useEffect(() => {
    return () => {
      saveImmediately()
    }
  }, [saveImmediately])

  useEffect(() => {
    if (!onBeforeUnload || !enabled) {
      return
    }

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      saveImmediately()

      if (hasPendingSaveRef.current || saveTimeoutRef.current) {
        e.preventDefault()
        e.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [onBeforeUnload, enabled, saveImmediately])

  return {
    saveImmediately,
    hasPendingSave: hasPendingSaveRef.current,
  }
}
